import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, downloadFile, errMsg } from "@/lib/api";
import { LindsayReply, Markdown, PageHeader, StatusBadge, fmtDate } from "@/components/Bits";

const AnswerForm = ({ item, kind, onDone }) => {
  const [answer, setAnswer] = useState("");
  const [addKb, setAddKb] = useState(true);
  const [kbTitle, setKbTitle] = useState("");
  const [kbCat, setKbCat] = useState("General");
  const [kbContent, setKbContent] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (answer.trim().length < 2) return toast.error("Write your answer first.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/${kind}/${item.id}/answer`, { answer, add_to_kb: addKb, kb_title: kbTitle, kb_category: kbCat, kb_content: kbContent });
      toast.success(addKb ? "Sent, and added to the knowledge bank." : "Sent to the member.");
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-t border-border pt-5 space-y-3">
      <Textarea rows={5} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Your answer, in your words…" data-testid={`answer-input-${item.id}`} className="bg-white" />
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={addKb} onCheckedChange={(v) => setAddKb(!!v)} data-testid={`answer-add-kb-${item.id}`} />
        Turn this into a knowledge bank entry
      </label>
      {addKb && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={kbTitle} onChange={(e) => setKbTitle(e.target.value)} placeholder="Topic title, e.g. 'Filling quiet Mondays'" data-testid={`answer-kb-title-${item.id}`} className="bg-white" />
            <Input value={kbCat} onChange={(e) => setKbCat(e.target.value)} placeholder="Category" data-testid={`answer-kb-category-${item.id}`} className="bg-white" />
          </div>
          <Textarea rows={4} value={kbContent} onChange={(e) => setKbContent(e.target.value)} placeholder="Knowledge bank version (leave blank to use your answer)" data-testid={`answer-kb-content-${item.id}`} className="bg-white" />
          <p className="text-xs text-muted-foreground">Only your advice is saved, never the member's question or name. Remove anything here that could identify them.</p>
        </>
      )}
      <Button onClick={send} disabled={busy} data-testid={`answer-submit-${item.id}`} className="rounded-full bg-navy hover:bg-navy/90 text-white">Send answer</Button>
    </div>
  );
};

const ReviewForm = ({ item, kind, aiText, onDone }) => {
  const [text, setText] = useState(aiText || "");
  const [busy, setBusy] = useState(false);
  const approve = async (edited) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/${kind}/${item.id}/approve`, { text: edited ? text : null });
      toast.success(edited ? "Approved with your edits" : "Approved and released");
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!aiText) return <p className="mt-4 text-sm text-muted-foreground animate-pulse">The AI is still drafting this one…</p>;
  return (
    <div className="mt-5 space-y-3">
      <div className="eyebrow">AI draft · edit before releasing if you like</div>
      <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} data-testid={`review-text-${item.id}`} className="bg-white font-sans text-sm" />
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => approve(false)} disabled={busy} data-testid={`review-approve-${item.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Approve as is</Button>
        <Button onClick={() => approve(true)} disabled={busy || text.trim() === aiText.trim()} variant="outline" data-testid={`review-approve-edited-${item.id}`} className="rounded-full">Approve with my edits</Button>
      </div>
      <details className="pt-2">
        <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider text-muted-foreground" data-testid={`review-reply-personally-${item.id}`}>Or reply personally instead</summary>
        <AnswerForm item={item} kind={kind} onDone={onDone} />
      </details>
    </div>
  );
};

const Item = ({ item, kind, onDone }) => {
  const isQ = kind === "questions";
  const aiText = isQ ? item.ai_answer : item.ai_feedback;
  const reply = isQ ? item.lindsay_answer : item.lindsay_note;
  const inReview = item.status === "in_review";
  return (
    <article className="rounded-2xl border border-border bg-card p-6 lg:p-8" data-testid={`queue-item-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <span className="text-xs font-mono text-muted-foreground">{item.user_name} · {item.business_name || "—"} · {fmtDate(item.created_at)}</span>
        <div className="flex items-center gap-2">
          {item.kb_covered === false && <span className="text-[10px] font-mono uppercase text-rose-600">KB gap</span>}
          <StatusBadge status={item.status} />
        </div>
      </div>
      <h3 className="text-lg font-semibold">{isQ ? item.question : item.title}</h3>
      {!isQ && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{item.description}</p>}
      {item.member_note && <p className="mt-3 text-sm italic text-muted-foreground">Member note: {item.member_note}</p>}
      {aiText && !inReview && (
        <details className="mt-4 rounded-xl border border-border bg-secondary p-4">
          <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider text-muted-foreground">AI draft the member saw</summary>
          <div className="mt-3"><Markdown>{aiText}</Markdown></div>
        </details>
      )}
      {isQ && item.followups?.length > 0 && (
        <details className="mt-3 rounded-xl border border-border bg-secondary p-4" data-testid={`queue-followups-${item.id}`}>
          <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider text-muted-foreground">Follow-ups ({item.followups.length})</summary>
          <div className="mt-3 space-y-4">{item.followups.map((f) => (
            <div key={f.id} className="border-l-2 border-pinkb/40 pl-3"><p className="text-sm font-medium mb-2">{f.question}</p><Markdown>{f.answer}</Markdown></div>
          ))}</div>
        </details>
      )}
      {reply ? <LindsayReply text={reply} /> : inReview ? <ReviewForm item={item} kind={kind} aiText={aiText} onDone={onDone} /> : item.status === "escalated" ? <AnswerForm item={item} kind={kind} onDone={onDone} /> : null}
    </article>
  );
};

export default function AdminQueue() {
  const [kind, setKind] = useState("questions");
  const [filter, setFilter] = useState("in_review");
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/${kind}`, { params: filter === "all" ? {} : { status: filter } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [kind, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Escalation queue" title="Over to you, Lindsay.">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="questions" data-testid="queue-tab-questions">Questions</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>
      <div className="flex gap-2 mb-8">
        {[["in_review", "Needs approval"], ["escalated", "Waiting for you"], ["lindsay_answered", "Answered by you"], ["all", "Everything"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`queue-filter-${v}`}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${filter === v ? "border-pinkb text-pinkb bg-pinkb/10" : "border-border text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-6">
        {items.map((it) => <Item key={it.id} item={it} kind={kind} onDone={(n) => setItems((l) => l.map((x) => (x.id === n.id ? n : x)))} />)}
        {items.length === 0 && <p className="text-muted-foreground text-sm" data-testid="queue-empty">Nothing here. Lovely.</p>}
      </div>
    </div>
  );
}
